<template>
  <div :class="[`${name}-wrapper`, $attrs.class]">
    <Field
      v-slot="{ field, errors, validate, meta }"
      :model-value="modelValue"
      @update:model-value="$emit('update:modelValue', $event)"
      :validate-on-mount="false"
      :validate-on-model-update="false"
      :name="name"
      :required="required"
      v-bind="bindValue"
      :readonly="readonly"
    >
      <label
        :class="{
          error: hasError(errors),
          disabled: disabled,
          required: isRequired,
        }"
      >
        <slot name="label" />
        <slot name="label-status">
          <template v-if="required">*</template>
          <translate v-else class="label-help"> (optional)</translate>
        </slot>
        <input
          :type="type"
          :required="required"
          v-bind="{ ...field, ...filteredAttrs }"
          @input="handleInput($event, validate, meta)"
          @blur="$emit('blur')"
        />
      </label>
      <input-error-message
        v-if="!hideIndividualErrors"
        :has-error="hasError(errors)"
      >
        <slot
          v-if="errors.length"
          name="errorMessage"
          :failed-rules="errors[0]"
        />
        <slot
          v-else-if="!!extractedValidationError"
          name="errorMessage"
          :failed-rules="extractedValidationError"
        >
          {{ extractedValidationError }}
        </slot>
      </input-error-message>
      <p v-if="$slots.helpText" class="help-text">
        <slot name="helpText" />
      </p>
    </Field>
  </div>
</template>
<script lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { Field } from 'vee-validate'
import { ZodType } from 'zod'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'

@Component({
  inheritAttrs: false,
  emits: ['update:modelValue', 'blur', 'input'],
  components: { Field },
})
class BaseInput extends Vue {
  @Prop()
  modelValue!: string

  @Prop()
  name!: string

  @Prop({ default: 'text' })
  type?: string

  @Prop()
  disabled!: boolean

  @Prop({ default: () => [] })
  validationErrors: string[]

  @Prop({ default: false })
  required?: boolean | string

  @Prop()
  validationRules!: ZodType

  @Prop({ default: false })
  readonly!: boolean

  @Prop({ default: false })
  hideIndividualErrors?: boolean

  get isRequired() {
    return !!this.required || this.required === ''
  }

  get labelText() {
    return this.$slots.label?.length ? this.$slots.label[0].text : ''
  }

  get filteredAttrs() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onInput, ...attrs } = this.$attrs
    return attrs
  }

  get bindValue() {
    const values: Record<string, any> = {}
    if (this.validationTypedSchema) {
      values.rules = this.validationTypedSchema
    }
    return values
  }

  get validationTypedSchema() {
    if (this.validationRules) {
      return toTypedSchema(this.validationRules)
    }
    return undefined
  }

  get extractedValidationError() {
    if (Array.isArray(this.validationErrors)) {
      return this.validationErrors[0]
    }
    return this.validationErrors
  }

  hasError(errors?: string[]) {
    return errors
      ? (this.validationErrors && this.validationErrors.length > 0) ||
          errors.length > 0
      : false
  }

  firstFailedRule(failedRules) {
    return failedRules[0] || undefined
  }

  handleInput(event: Event, validate, meta) {
    this.$emit('update:modelValue', event.target.value)
    this.$emit('input', event.target.value)
    if (meta.touched) {
      validate()
    }
  }
}

export default toNative(BaseInput)
</script>

<style scoped>
.label-help {
  color: var(--colour-text-light);
}
</style>
